import { Direction, IFilterInput, InputMaybe, Scalars } from '../../graphql/schema/p4types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryHookOptions, useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';

type IUseQueryForDetailsList = <TData = any, TVariables = any>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables>
) => { data: TData; queueRequestFor: (rowIndex: number) => void };

export type ICommonRequestResult = {
  list: { count?: any; edges?: Array<{ node?: any }>; pageInfo: { hasNextPage: boolean; endCursor?: string } };
};
export type ICommonRequestVariables = {
  first?: number;
  after?: `${number}`;
  filters?: Array<IFilterInput>;
};
export const QUERY_PAGE_SIZE = 40;

const useQueryForDetailsList: IUseQueryForDetailsList = (query, options) => {
  const queueHistory = useRef<{ [key: number]: boolean }>({});
  //Reset queue history if variables change.
  useEffect(() => {
    queueHistory.current = {};
  }, [options.variables]);

  const { data, fetchMore } = useQuery<ICommonRequestResult, ICommonRequestVariables>(
    query,
    options as unknown as QueryHookOptions<ICommonRequestResult, ICommonRequestVariables>
  );

  const optionVariables = options.variables as any;

  const queueRequestFor = useCallback(
    (rowIndex: number) => {
      const pageTarget = (rowIndex / QUERY_PAGE_SIZE) >> 0;
      if (queueHistory.current[pageTarget]) {
        return;
      }
      queueHistory.current[pageTarget] = true;
      const after = pageTarget * QUERY_PAGE_SIZE;
      if (after > 0) {
        fetchMore({
          variables: {
            ...optionVariables,
            first: QUERY_PAGE_SIZE,
            after: after,
          },
        });
      }
    },
    [fetchMore, optionVariables]
  );

  return useMemo(() => ({ data: data as any, queueRequestFor }), [data, queueRequestFor]);
};

export default useQueryForDetailsList;
